// utils/DataCollectorForTests.js

class DataCollectorForTests {
  constructor() {
    this.rawData = {
      configTiroirs: window.app?.state?.data?.sources?.configTiroirs?.data?.references || [],
      baseROP: window.app?.state?.data?.sources?.baseROP?.data?.references || [],
      rapportFiberPvReussite: window.app?.state?.data?.sources?.rapportFiberPvReussite?.data?.references || [],
      pvReussite: window.app?.state?.data?.sources?.pvReussite?.data?.fibers || [],
      rapportFiberPvEchec: window.app?.state?.data?.sources?.rapportFiberPvEchec?.data?.references || [],
      pvEchec: window.app?.state?.data?.sources?.pvEchec?.data?.fibers || [],
      pvHs: window.app?.state?.data?.sources?.pvHs?.data?.fibers || [],
      conflictResolutions: window.app?.state?.data?.sources?.conflictResolutions?.data?.fibers || [],
    };
  }

  /**
   * ÉTAPE 1: Construire les index de toutes les sources
   * Une seule passe par source, accès O(1) ensuite
   */
  _buildIndexes() {
    const indexes = {
      configTiroirs: new Map(),
      baseROP: new Map(),
      rapportFiberPvReussite: new Map(),
      pvReussite: new Map(),
      rapportFiberPvEchec: new Map(),
      pvEchec: new Map(),
      pvHs: new Map(),
      conflictResolutions: new Map(),
    };

    // Index configTiroirs
    this.rawData.configTiroirs.forEach((config) => {
      const key = `${config.TIROIR}-${config.MODULE}-${config.PORT}`;
      indexes.configTiroirs.set(key, config);
    });

    // Index baseROP
    this.rawData.baseROP.forEach((rop) => {
      const refParts = rop.REFERENCE.split("-");
      const tiroir = refParts[1]; // T1 depuis B2-T1-MODULE-A
      const module = refParts[3]; // A depuis B2-T1-MODULE-A
      const key = `${tiroir}-${module}-${rop.PORT}`;
      indexes.baseROP.set(key, rop);
    });

    // Index rapportFiber (DCO) des PV de réussite
    this.rawData.rapportFiberPvReussite.forEach((dco) => {
      const tiroir = this._normalizeTiroir(dco.TIROIR);
      const key = `${tiroir}-${dco.MODULE}-${dco.PORT}`;
      indexes.rapportFiberPvReussite.set(key, dco);
    });

    // Index pvReussite
    this.rawData.pvReussite.forEach((pv) => {
      const key = `${pv.tiroir}-${pv.module}-${pv.port}`;
      indexes.pvReussite.set(key, pv);
    });

    // Index rapportFiber (DCO) des PV d'echec
    this.rawData.rapportFiberPvEchec.forEach((dco) => {
      const tiroir = this._normalizeTiroir(dco.TIROIR);
      const key = `${tiroir}-${dco.MODULE}-${dco.PORT}`;
      indexes.rapportFiberPvEchec.set(key, dco);
    });

    // Index pvEchec
    this.rawData.pvEchec.forEach((pv) => {
      const key = `${pv.tiroir}-${pv.module}-${pv.port}`;
      indexes.pvEchec.set(key, pv);
    });

    // Index Fiber Hors Servie
    this.rawData.pvHs.forEach((pv) => {
      const key = `${pv.tiroir}-${pv.module}-${pv.port}`;
      indexes.pvHs.set(key, pv);
    });

    // Index Fiber Conflits
    this.rawData.conflictResolutions.forEach((conflit) => {
      // Parser la REFERENCE comme "B2-T1-MA"
      const refParts = conflit.REFERENCE.split("-");
      const tiroir = refParts[1]; // T1
      const module = refParts[2]; // MA
      const port = conflit.PORT; // 11
      const key = `${tiroir}-${module[1]}-${port}`;
      indexes.conflictResolutions.set(key, conflit);
    });

    return indexes;
  }

  /**
   * ÉTAPE 2: Collecter toutes les clés uniques de fibres
   */
  _getAllFiberKeys(indexes) {
    const allKeys = new Set();

    // Ajouter toutes les clés de tous les index
    indexes.configTiroirs.forEach((_, key) => allKeys.add(key));
    indexes.baseROP.forEach((_, key) => allKeys.add(key));
    indexes.rapportFiberPvReussite.forEach((_, key) => allKeys.add(key));
    indexes.pvReussite.forEach((_, key) => allKeys.add(key));
    indexes.rapportFiberPvEchec.forEach((_, key) => allKeys.add(key));
    indexes.pvEchec.forEach((_, key) => allKeys.add(key));
    indexes.pvHs.forEach((_, key) => allKeys.add(key));
    indexes.conflictResolutions.forEach((_, key) => allKeys.add(key));

    return Array.from(allKeys);
  }

  /**
   * ÉTAPE 3: Construire les objets fiber enrichis avec flags précis
   */
  _buildEnrichedFibers(allFiberKeys, indexes) {
    return allFiberKeys.map((fiberKey) => {
      const [tiroir, module, port] = fiberKey.split("-");
      const portNum = parseInt(port);

      // Récupération directe depuis les index (O(1))
      const config = indexes.configTiroirs.get(fiberKey);
      const ropRef = indexes.baseROP.get(fiberKey);
      const dcoPvReussite = indexes.rapportFiberPvReussite.get(fiberKey);
      const pvReussite = indexes.pvReussite.get(fiberKey);
      const dcoPvEchec = indexes.rapportFiberPvEchec.get(fiberKey);
      const pvEchec = indexes.pvEchec.get(fiberKey);
      const pvHs = indexes.pvHs.get(fiberKey);
      const conflictResolutions = indexes.conflictResolutions.get(fiberKey);

      // Calcul des flags de présence
      const hasConfigTiroir = !!config;
      const hasRopRef = !!ropRef;
      const hasDcoPvReussite = !!dcoPvReussite;
      const hasPvReussite = !!pvReussite;
      const hasDcoPvEchec = !!dcoPvEchec;
      const hasPvEchec = !!pvEchec;
      const hasPvHs = !!pvHs;
      const hasConflictResolutions = !!conflictResolutions;

      const fiber = {
        // Identification
        id: fiberKey,
        referenceShort: config?.REFERENCE_SHORT || `B2-${tiroir}-M${module}`,
        referenceLong: config?.REFERENCE_LONG || `B2-${tiroir}-MODULE-${module}`,
        tiroir: tiroir,
        module: module,
        port: portNum,
        etat: config?.ETAT || "inconnu",

        // Données sources
        ...(config !== undefined && { config }),
        ...(ropRef !== undefined && { ropRef }),
        ...(dcoPvReussite !== undefined && { dcoPvReussite }),
        ...(pvReussite !== undefined && { pvReussite }),
        ...(dcoPvEchec !== undefined && { dcoPvEchec }),
        ...(pvEchec !== undefined && { pvEchec }),
        ...(pvHs !== undefined && { pvHs }),
        ...(conflictResolutions !== undefined && { conflictResolutions }),

        // Flags de présence (c'est ça qui compte pour détecter les orphelins)
        hasConfigTiroir,
        hasRopRef,
        hasDcoPvReussite,
        hasPvReussite,
        hasDcoPvEchec,
        hasPvEchec,
        hasPvHs,
        hasConflictResolutions,

        // Résultats des tests (à remplir par vos règles métier)
        testResults: {
          status: "pending",
          messages: [],
          hasError: false,
          hasWarning: false,
        },
      };

      return fiber;
    });
  }

  /**
   * Collecter et préparer toutes les données avec détection d'orphelins
   */
  collectAndPrepare() {
    // 1. Construire les index (une passe par source)
    const indexes = this._buildIndexes();

    // 2. Collecter toutes les clés uniques
    const allFiberKeys = this._getAllFiberKeys(indexes);

    // 3. Construire les objets enrichis
    const fibers = this._buildEnrichedFibers(allFiberKeys, indexes);

    // 4. Trier les fibres par tiroir, module, puis port
    const sortedFibers = fibers.sort((a, b) => {
      // Comparer d'abord par tiroir
      if (a.tiroir !== b.tiroir) {
        return a.tiroir.localeCompare(b.tiroir);
      }
      // Puis par module
      if (a.module !== b.module) {
        return a.module.localeCompare(b.module);
      }
      // Enfin par port (numérique)
      return a.port - b.port;
    });

    // 3. Construire les objets enrichis
    return sortedFibers;
  }

  /**
   * Normaliser les identifiants de tiroir
   */
  _normalizeTiroir(tiroir) {
    return tiroir.startsWith("T") ? tiroir : `T${tiroir}`;
  }

  /**
   * Obtenir des statistiques sur les données collectées
   */
  getStats() {
    const fibers = this.collectAndPrepare();

    return {
      totalFibers: fibers.length,
      withDCO: fibers.filter((f) => f.hasDcoPvReussite).length,
      withROP: fibers.filter((f) => f.hasRopRef).length,
      withPvOK: fibers.filter((f) => f.hasPvReussite).length,
      withPvNOK: fibers.filter((f) => f.hasPvEchec).length,
      withConfigTiroir: fibers.filter((f) => f.hasConfigTiroir).length,
      byState: {
        libre: fibers.filter((f) => f.etat?.toLowerCase() === "libre").length,
        occupee: fibers.filter((f) => f.etat?.toLowerCase() === "occupee" || f.etat?.toLowerCase() === "occupée")
          .length,
        inconnu: fibers.filter((f) => f.etat === "inconnu").length,
      },
    };
  }
}

export default DataCollectorForTests;
